import React, { useState, useEffect, forwardRef, useCallback, useRef, useMemo } from "react";
import { makeStyles } from "tss-react/mui";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Virtuoso } from "react-virtuoso";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Checkbox from "@mui/material/Checkbox";

import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";

import WorkspaceClass from "../../../service/workspace";
import DataSourceClass from "../../../service/dataSource";
import DataPipeline from "../../../service/dataPipelineService";

import PreviewTable from "../../Common/Mapping/PreviewTable";
import { ButtonUI, KMKDialog, ConfirmPopup } from "../../Common";
import { showToast, hideMappingPopup, upload, loadFromCloudStorage } from "../../../store/actions";
import { identifyDataTypeAndFormat, setPrevState, parseFile } from "../../../util/common";
import { regexName, regexSnowflakeField } from "../../../util/regularExpress";
import { checkReservedKeywords } from "../../../util/restrictedStatement";
import { parse } from "papaparse";
import { DATA_SOURCE } from "../../../util/Data/DataModeling";
import { DETAUL_CRON_VALUE, JOB_PURPOSE_EXCHANGE } from "../scheduledJobs/utils/JobData";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: "24px 24px 24px 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    height: "100%",
    marginLeft: "0",
    marginBottom: "0",
    display: "flex",
    flexDirection: "column",
  },
  Toolbar: {
    backgroundColor: "white",
    color: "black",
    padding: 0,
  },
  TableHead: {
    position: "sticky",
    top: "0px",
    zIndex: 2,
    backgroundColor: "rgb(255, 255, 255)",
    whiteSpace: "nowrap",
  },
  PanelContainer: {
    padding: "0",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "4px",
      "&::-webkit-scrollbar-track": {
        marginTop: "36px",
      },
    },
    "&.MuiGrid-item": {
      paddingTop: 0,
    },
  },
  Panel: {
    borderRadius: "4px",
    marginBottom: 0,
  },
  PanelTitle: {
    padding: 5,
  },
  mappingContainer: {
    overflow: "auto",
    marginTop: "16px",
    height: "calc(100% - 62px)",
  },
  mappingHeader: {
    fontWeight: "700",
    width: "max(24.25%, 200px)",
    display: "flex",
    justifyContent: "center",
  },
  mappingField: {
    "& legend": { display: "none" },
    "& fieldset": { top: 0 },
  },
  dialogContent: {
    padding: "0",
    overflow: "hidden",
  },
  innerDialog: {
    "& .MuiPaper-root": {
      width: "100%",
    },
  },
}));

//Add job
const AddJob = ({ loading, onClose, onSave }) => {
  const [job, setJob] = useState("");
  const { classes } = useStyles();

  return (
    <KMKDialog maxWidth="xs" fullWidth open={true} className={classes.innerDialog}>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar
          sx={{
            fontSize: "1.1rem",
            backgroundColor: "white",
            color: "black",
          }}
        >
          Save as New Job
        </Toolbar>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <DialogContent>
        <Box>
          <TextField
            value={job}
            margin="dense"
            inputProps={{ maxLength: 30 }}
            onChange={(e) => {
              const isValid = regexName(e.target.value, "jobName", "_ -");
              if (isValid) setJob(e.target.value);
            }}
            autoFocus
            label="Name"
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingRight: "20px" }}>
        <ButtonUI
          variant="contained"
          color="success"
          style={{ marginLeft: 15 }}
          disabled={!job.trim()}
          onClick={() => onSave(job)}
        >
          Save
        </ButtonUI>
        <ButtonUI variant="contained" size="medium" color="secondary" onClick={onClose}>
          Cancel
        </ButtonUI>
      </DialogActions>
    </KMKDialog>
  );
};

//Add Data source
const AddDataSource = ({ loading, onClose, onSave, systemNameError }) => {
  const [displayName, setDisplyaName] = useState("");
  const [tableName, setTableName] = useState("");

  const { classes } = useStyles();

  return (
    <KMKDialog maxWidth="xs" fullWidth open={true} className={classes.innerDialog}>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar
          sx={{
            fontSize: "1.1rem",
            backgroundColor: "white",
            color: "black",
          }}
        >
          {`Save as ${DATA_SOURCE.singular.toLowerCase()}`}
        </Toolbar>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <DialogContent>
        <Box>
          <TextField
            value={displayName}
            margin="dense"
            inputProps={{ maxLength: 50 }}
            onChange={(e) => {
              setDisplyaName(e.target.value);
            }}
            autoFocus
            label="Display Name"
            fullWidth
            required
          />
        </Box>
        <Box>
          <TextField
            value={tableName}
            margin="dense"
            inputProps={{ maxLength: 50 }}
            onChange={(e) => {
              const isValid = regexName(e.target.value, "alphaWithUnderscore");
              if (isValid) setTableName(e.target.value);
            }}
            label="System Name"
            helperText={systemNameError}
            error={systemNameError}
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ paddingRight: "20px" }}>
        <ButtonUI
          variant="contained"
          color="success"
          style={{ marginLeft: 15 }}
          disabled={
            !displayName.trim() || displayName.trim().length < 2 || !tableName.trim() || tableName.trim().length < 2
          }
          onClick={() => onSave(displayName, tableName)}
        >
          Save
        </ButtonUI>
        <ButtonUI variant="contained" size="medium" color="secondary" onClick={onClose}>
          Cancel
        </ButtonUI>
      </DialogActions>
    </KMKDialog>
  );
};

const Mapping = (props) => {
  const {
    bucket,
    delimeter,
    destinationAction,
    fieldEnclosedBy,
    encoding,
    onError,
    onErrorNumber = 0,
    fileType,
    hasHeaderRow,
    location,
    locationName,
    path,
    schemaName,
    tableName,
    value,
    existingTableAction,
    uploadedFile,
    clearState,
    type,
    setShowResents,
    docuList,
    localFileChunk,
    cloudFileChunk,
    getCloudFileData,
    nullValue,
    saveAsDataset,
    currentName,
    displayName,
    unloadEnabled,
    unloadConnection,
    unloadPath,
  } = props;

  const append = destinationAction === "existing" && existingTableAction === "append";
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  const previewTableRef = useRef(null);
  const filterRef = useRef(null);
  const virtuosoRef = useRef(null);
  const { Workspace_id } = useParams();
  const {
    defaultSourceValues,
    defaultCluster,
    attributeDataTypes,
    encodingTypes,
    previewCount = 100,
  } = useSelector((state) => state.config);
  const workspace = useSelector((state) => state.workspace);
  const accessRole = workspace.workspace.accessRole;

  const {
    compression: defaultCompression,
    encoding: defaultEncoding,
    qualifier: defaultFieldEnclosedBy,
    errorAction: defaultErrorAction,
  } = defaultSourceValues;

  const { options, formats, types } = useMemo(() => {
    const options = {};
    const formats = {};
    const types = [];

    attributeDataTypes.forEach((option) => {
      if (option.options) {
        if (option.type !== "Date" && option.type !== "DateTime" && option.type !== "Time")
          options[option.type] = option.options;
        else formats[option.type] = option.options;
      }

      if (option.type !== "Reference")
        types.push({
          value: option.type,
          text: option.type,
        });
    });

    return { options, formats, types };
  }, [attributeDataTypes]);

  const [openJob, setOpenJob] = useState(false);
  const [openDataSource, setOpenDataSource] = useState(false);
  const [state, setState] = useState({
    validMapping: false,
    loading: false,
  });
  const [columns, setColumns] = useState({
    headers: [],
    mappings: [],
    data: [],
    fileName: "",
    error: false,
  });

  const [filters, setFilters] = useState({
    search: "",
    dataType: "",
  });

  const [bulkChange, setBulkChange] = useState({
    open: false,
    source: "",
    value: {},
  });
  const [systemNameError, setSystemNameError] = useState(false);

  const { validMapping, loading } = state;

  const runningProcesses = useSelector((state) => state.runningProcesses);
  const { classes } = useStyles();

  //Close popup
  const handleCancel = () => dispatch(hideMappingPopup());

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setColumns((prev) => ({
      ...prev,
      mappings: prev.mappings.map((mapping, i) => {
        if ($mappings.includes(mapping)) return { ...mapping, isSelected: isChecked };
        return mapping;
      }),
    }));
  };

  const _setColumns = useCallback(
    (existingFields, headers, data, fileName = "", error = false) => {
      let columnsDataTypesAndFormat = {};
      if (data.length > 0) {
        columnsDataTypesAndFormat = identifyDataTypeAndFormat(headers, data);
      } else {
        if (existingFields) {
          existingFields?.forEach(({ name, size, type: dataType }) => {
            const _name = headers.find((header) => header.toLowerCase() === name.toLowerCase());
            columnsDataTypesAndFormat[_name || name] = {
              dataType,
              size,
              format: "",
            };
          });
        } else {
          headers?.forEach((field) => {
            columnsDataTypesAndFormat[field] = {
              dataType: "String",
              format: null,
            };
          });
        }
      }
      setColumns({
        fileName,
        headers,
        mappings: headers.map((field, i) => {
          const dataType = columnsDataTypesAndFormat[field]["dataType"];
          let format = "";
          let customFormat = "";
          if (formats[dataType] !== undefined) {
            let recivedFormat = columnsDataTypesAndFormat[field]["format"];
            if (recivedFormat !== undefined && recivedFormat !== null) {
              format = formats[dataType].find((el) => el.value === columnsDataTypesAndFormat[field]["format"]);
              if (format === undefined) {
                format = { value: "Custom" };
                customFormat = columnsDataTypesAndFormat[field]["format"];
              }
            }
          }

          let destinationName = HandleFields(
            append || (fileType === "parquet" && existingFields.length) ? existingFields[i].name : field
          );

          let hasError = false;
          let errorMessage = "";

          if (checkReservedKeywords(destinationName)) {
            hasError = true;
            errorMessage = "Reserved keyword";
          } else if (destinationName.length > 128) {
            hasError = true;
            errorMessage = "Max limit: 128 Characters!";
          }

          let size = columnsDataTypesAndFormat[field]["length"];
          if (append || (fileType === "parquet" && existingFields.length)) size = existingFields[i].size || "";
          if (!size) {
            const currOptions = options[dataType];
            if (currOptions) {
              const index = dataType === "Number" ? 0 : dataType === "String" ? 5 : currOptions.length - 1;
              size = currOptions[index].value;
            } else size = "";
          }

          return {
            source: field,
            destination: destinationName,
            type: append || (fileType === "parquet" && existingFields.length) ? existingFields[i].type : dataType,
            size,
            format: format ? format.value : "",
            customFormat: format && format.value === "Custom" ? customFormat : "",
            hasError,
            errorMessage,
            isSelected: false,
          };
        }),
        data,
        error,
      });
    },
    [append, fileType, formats, options]
  );

  const getS3FileColumns = useCallback(async () => {
    let headers = [];
    let data = [];
    let snowflakeFields = [];
    let fileName = "";
    let error = false;

    if (
      cloudFileChunk.path !== path ||
      cloudFileChunk.fileProperties?.delimiter !== delimeter ||
      cloudFileChunk.fileProperties?.encoding !== encoding ||
      cloudFileChunk.fileProperties?.fileType !== fileType ||
      cloudFileChunk.fileProperties?.fieldEnclosedBy !== fieldEnclosedBy ||
      cloudFileChunk.fileProperties?.hasHeaderRow !== hasHeaderRow ||
      !cloudFileChunk.sampleData ||
      !cloudFileChunk.fields ||
      (append && !cloudFileChunk?.snowflakeFields)
    ) {
      let response = await getCloudFileData(false, "");
      if (response.success) {
        headers = response?.data?.fields;
        data = response?.data?.sampleData;
        snowflakeFields = response?.data?.snowflakeFields || [];
        fileName = response?.data?.fileName || "";
        error = response?.data?.error || false;
      }
    } else {
      headers = cloudFileChunk?.fields;
      data = cloudFileChunk?.sampleData;
      snowflakeFields = cloudFileChunk?.snowflakeFields || [];
      fileName = cloudFileChunk?.fileName;
      error = cloudFileChunk?.error;
    }

    if (headers && data) {
      _setColumns(snowflakeFields, headers, data, fileName, error);
      setPrevState(setState, { validMapping: true });
    } else {
      dispatchRef.current(hideMappingPopup());
    }
  }, [
    cloudFileChunk.path,
    cloudFileChunk.fileProperties?.delimiter,
    cloudFileChunk.fileProperties?.encoding,
    cloudFileChunk.fileProperties?.fileType,
    cloudFileChunk.fileProperties?.fieldEnclosedBy,
    cloudFileChunk.fileProperties?.hasHeaderRow,
    cloudFileChunk.sampleData,
    cloudFileChunk.fields,
    cloudFileChunk?.snowflakeFields,
    cloudFileChunk?.fileName,
    cloudFileChunk?.error,
    path,
    delimeter,
    encoding,
    fileType,
    fieldEnclosedBy,
    hasHeaderRow,
    append,
    getCloudFileData,
    _setColumns,
  ]);

  //reading localfile and comparing with snowflake if appending
  const loadLocalFile = useCallback(async () => {
    let fileData = "";
    const lineCount = hasHeaderRow ? previewCount + 1 : previewCount;

    let snowflakeFields = false;
    //If appending then getting snowflake table fields
    if (append) {
      const workspace = new WorkspaceClass();
      const response = await workspace.loadSnowflakeFields(Workspace_id, { schemaName, tableName });

      if (response.success) snowflakeFields = response.data;
      else dispatchRef.current(hideMappingPopup());
    }

    //Parsing file and reading first X lines of file
    if (!append || (append && snowflakeFields !== false)) {
      const parseAndMap = (fileData) =>
        parse(fileData, {
          delimiter: delimeter,
          newline: "", // auto-detect
          quoteChar: fieldEnclosedBy,
          nullValue: fieldEnclosedBy ? (fieldEnclosedBy === "'" ? "''" : '""') : "default",
          header: false,
          preview: lineCount,
          complete: (result) => {
            let previewData = result.data;
            //Getting/Creating header rows
            const headerField = hasHeaderRow
              ? previewData[0]
              : [...Array(previewData[0].length).keys()].map((i) => "column" + (i + 1));
            //If appending then Checking table and files has same number of columns
            if (append && headerField.length !== snowflakeFields.length) {
              dispatchRef.current(hideMappingPopup());
              return dispatchRef.current(
                showToast({
                  message: "Invalid source destination columns count!",
                  type: "error",
                })
              );
            }
            //Extracting first X lines from data
            const dataLines = hasHeaderRow
              ? previewData.slice(1, previewCount + 1)
              : previewData.slice(0, previewCount);

            _setColumns(snowflakeFields, headerField, dataLines, localFileChunk?.fileName, false);

            setPrevState(setState, { validMapping: true });
          },
        });

      if (localFileChunk.fileType === "parquet") {
        _setColumns(snowflakeFields, localFileChunk.data.headers, localFileChunk.data.data, localFileChunk?.fileName);
        setPrevState(setState, { validMapping: true });
      } else if (
        localFileChunk.data !== undefined &&
        localFileChunk.data !== null &&
        localFileChunk.encoding === encoding &&
        localFileChunk.delimiter === delimeter &&
        localFileChunk.nullValue === nullValue &&
        localFileChunk.fieldEnclosedBy === fieldEnclosedBy &&
        localFileChunk.fileType === fileType
      )
        parseAndMap(localFileChunk.data);
      else
        parseFile(
          uploadedFile,
          (data, readMore) => {
            fileData += data;
            //Checking if file is empty
            if (fileData === "") {
              dispatchRef.current(hideMappingPopup());
              return dispatchRef.current(
                showToast({
                  message: "File is empty!",
                  type: "error",
                })
              );
            }
            parseAndMap(fileData);
          },
          false,
          encodingTypes.find((en) => en.value === encoding).text ?? "UTF-8"
        );
    }
  }, [
    hasHeaderRow,
    previewCount,
    append,
    Workspace_id,
    schemaName,
    tableName,
    localFileChunk.fileType,
    localFileChunk.data,
    localFileChunk.encoding,
    localFileChunk.delimiter,
    localFileChunk.nullValue,
    localFileChunk.fieldEnclosedBy,
    localFileChunk?.fileName,
    encoding,
    delimeter,
    nullValue,
    fieldEnclosedBy,
    fileType,
    uploadedFile,
    encodingTypes,
    _setColumns,
  ]);

  const HandleFields = (field) => {
    // Replace space with underscore(_) if field name contains space( )
    field = field.trim().replaceAll(/[^a-zA-Z0-9_$]/gi, "_");

    // Replace first chart with underscore(_) If field Name is not starting with Alphabet or underscore
    const regEx = /^[a-zA-Z_].*/;
    if (!regEx.test(field)) field = `_${field}`;

    return field;
  };

  const handleBulkChange = (changeAll) => {
    if (changeAll) {
      const value = bulkChange.value;
      const formatValue = formats[value.type]
        ? formats[value.type].find((v) => v.value === value.format)?.value ??
          formats[value.type][formats[value.type].length - 1]?.value
        : "";
      setColumns((prev) => ({
        ...prev,
        mappings: prev.mappings.map((mapping) => {
          if (mapping.isSelected && $mappings.includes(mapping))
            return {
              ...mapping,
              ...value,
              size: options[value.type]
                ? options[value.type].find((v) => v.value === value.size)?.value ??
                  options[value.type][
                    value.type === "Number" ? 0 : value.type === "String" ? 5 : options[value.type].length - 1
                  ]?.value
                : "",
              format: formatValue,
              customFormat:
                formatValue === "Custom" ? value.customFormat : value.type === mapping.type ? mapping.customFormat : "",
            };
          else return { ...mapping, isSelected: false };
        }),
      }));
    } else updateMapping(bulkChange.value, bulkChange.source);

    setPrevState(setBulkChange, { open: false, value: {}, source: "" });
  };

  const updateMapping = useCallback(
    (value, source) => {
      if (value["destination"]) value["destination"] = value["destination"].replaceAll(" ", "");

      setColumns((prev) => {
        return {
          ...prev,
          mappings: prev.mappings.map((_mapping) =>
            _mapping.source === source
              ? {
                  ..._mapping,
                  ...value,
                  ...(value.type && _mapping.type !== value.type
                    ? {
                        size: options[value.type]
                          ? options[value.type][
                              value.type === "Number" ? 0 : value.type === "String" ? 5 : options[value.type].length - 1
                            ].value
                          : "",
                        format: formats[value.type] ? formats[value.type][formats[value.type].length - 1].value : "",
                        customFormat: "",
                      }
                    : {}),
                }
              : _mapping
          ),
        };
      });
    },
    [formats, options]
  );

  useEffect(() => {
    (async () => {
      if (value === 0) loadLocalFile();
      else getS3FileColumns();
    })();

    return () => {
      setState({
        validMapping: false,
        loading: false,
      });
      setColumns({
        headers: [],
        mappings: [],
        data: [],
        fileName: "",
        error: false,
      });
    };
  }, [value, loadLocalFile, getS3FileColumns, options, formats]);

  //When Preview popup is opened then auto scroll to first reserved keyword if found
  useEffect(() => {
    if (state.validMapping) {
      let scrollIndex = null;
      $mappings.forEach(({ hasError }, index) => {
        if (hasError) scrollIndex = index;
      });
      if (scrollIndex) virtuosoRef.current.scrollToIndex(scrollIndex);
    }

    //For first time only so not adding dependency for columns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.validMapping]);

  //Validate Destination Columns string which allows alphanumeric, underscore and doller
  const validateDestinationCols = (colMappings) => {
    let valid = false;

    //Find all reserved keywords and auto scroll to first reserved keyword if found
    let scrollIndex = null;
    let reservedFlags = colMappings.filter(({ source, destination, hasError }, index) => {
      const isReserved = checkReservedKeywords(destination);
      if (isReserved && scrollIndex === null) scrollIndex = index;
      if (isReserved !== hasError)
        updateMapping({ hasError: isReserved, errorMessage: isReserved ? "Reserved keyword" : "" }, source);
      return isReserved;
    });
    if (scrollIndex) virtuosoRef.current.scrollToIndex({ index: scrollIndex, align: "start", behavior: "smooth" });

    //Validating unique columns
    const uniqueColumns = new Set(colMappings.map(({ destination }) => destination.toLowerCase()));
    if (colMappings.length !== uniqueColumns.size)
      dispatch(
        showToast({
          message: "Destination columns are not unique!",
          type: "error",
        })
      );
    else if (colMappings.some((column) => !column.destination.trim()))
      dispatch(
        showToast({
          message: "Destination columns can not be empty!",
          type: "error",
        })
      );
    else if (reservedFlags.length)
      dispatch(
        showToast({
          message: `${reservedFlags.length} destination column${
            reservedFlags.length > 1 ? "s" : ""
          } contain reserved keyword${reservedFlags.length > 1 ? "s" : ""}!`,
          type: "error",
        })
      );
    else if (colMappings.some((column) => column.destination.length > 128)) {
      setColumns((prev) => {
        return {
          ...prev,
          mappings: prev.mappings.map((mapping, i) => {
            const hasError = mapping.destination.length > 128;
            if (!scrollIndex && hasError) {
              scrollIndex = i;
              virtuosoRef.current.scrollToIndex({ index: scrollIndex, align: "start", behavior: "smooth" });
            }

            return hasError ? { ...mapping, hasError, errorMessage: "Max limit: 128 Characters!" } : mapping;
          }),
        };
      });

      dispatch(
        showToast({
          message: "Length of the Destination Column should be less than 128!",
          type: "error",
        })
      );
    } else if (
      append &&
      colMappings.some(
        (column) =>
          column.size &&
          options[column.type] &&
          !options[column.type].some(({ value }) => parseInt(value) === parseInt(column.size))
      )
    ) {
      dispatch(
        showToast({
          message: "Error while processing, please check the column size!",
          type: "error",
        })
      );
    } else if (
      colMappings.some(
        (column) =>
          column.format &&
          formats[column.type] &&
          column.format === "Custom" &&
          (column.customFormat === "" || column.customFormat === null || column.customFormat === undefined)
      )
    ) {
      dispatch(
        showToast({
          message: "Custom format cant't be empty, please provide custom format",
          type: "error",
        })
      );
    } else valid = true;

    return valid;
  };

  //handling click on Save as data source
  const handleCreateDataSource = async (displayName, tableName) => {
    if (checkReservedKeywords(tableName)) return setSystemNameError("Reserved keyword.");
    else setSystemNameError(false);
    if (!validateDestinationCols(columns.mappings)) return;
    setPrevState(setState, { loading: true });
    const dataSource = new DataSourceClass();
    const { success, data } = await dataSource.getAllObjectNames(Workspace_id);
    if (!success) return;

    const allObjects = [...(data.sources || []), ...(data.entities || [])];

    // Checking for name validation (display name should be unique accross dataset only)
    if (
      (data.sources || []).some(
        (dataSource) => dataSource.displayName?.toLowerCase().trim() === displayName.toLowerCase().trim()
      )
    ) {
      //checking for unique name of display name
      setPrevState(setState, { loading: false });
      return dispatch(
        showToast({
          message: "Display name must be Unique.",
          type: "error",
        })
      );
    } else if (allObjects.some((source) => source.name.toLowerCase() === tableName.toLowerCase())) {
      const dsName = allObjects.find((source) => source.name.toLowerCase() === tableName.toLowerCase()).displayName;
      //checking for unique name of table name
      setPrevState(setState, { loading: false });

      return dispatch(
        showToast({
          message: `Table name must be Unique (In use for ${DATA_SOURCE.singular}: ${dsName}).`,
          type: "error",
        })
      );
    }

    //Generating fields array
    const fields = columns.mappings.map((mapping) => {
      return {
        name: mapping.destination.trim(),
        type: mapping.type,
        description: "",
        length: mapping.size ? mapping.size : "",
        format: mapping.format,
        customFormat: mapping.customFormat,
        primary_attribute: false,
        secondary_attribute: false,
        entity_mapping: {},
        dqm: { required: false },
        required: true,
      };
    });

    //Creating datasourec object
    const connectionName = "ds_" + tableName;
    const dataSourceObj = {
      connection_ref: connectionName,
      name: tableName,
      displayName: displayName.trim(),
      description: "",
      fields,
      shareWithDC: false,
      onError: onError || defaultErrorAction,
      onErrorNumber: onErrorNumber || 0,
      datasetType: "untracked",
      format: {
        name: `${tableName}_FileFormat`,
        type: fileType.toLowerCase(),
        delimiter: delimeter,
        nullValue,
        fieldEnclosedBy: fieldEnclosedBy || defaultFieldEnclosedBy,
        encoding: encoding || defaultEncoding,
        compression: defaultCompression,
        header: hasHeaderRow ? "yes" : "no",
      },
    };

    //Creating connection
    const connectionObj = {
      name: connectionName,
      objectType: "File",
      resetFiles: false,
      ...(value === 1 && type !== "sftp" && type !== "sharepoint" && type !== "mysql"
        ? { type, location: locationName, path }
        : {
            type: "",
            location: "",
            path: "",
          }),
    };

    await handleSubmit(dataSourceObj, connectionObj, { saveAsDataset: true, displayName, tableName });

    setOpenDataSource(false);

    setPrevState(setState, { loading: false });
  };

  //Create job
  const handleCreateJob = async (job) => {
    setPrevState(setState, { loading: true });

    const wsService = new WorkspaceClass();
    const pipeline = new DataPipeline();

    const defaultPipeline = await pipeline.getDefaultPipeline(Workspace_id);
    if (!defaultPipeline?.data?.DataPipeline_id) {
      setOpenJob(false);
      setPrevState(setState, { loading: false });
      return;
    }
    const DataPipeline_id = defaultPipeline?.data?.DataPipeline_id;

    let submitData = {
      Workspace_id,
      DataPipeline_id,
      jobPurpose: JOB_PURPOSE_EXCHANGE,
      name: job,
      dest_stage_id: "SNOWFLAKE",
      source_stage_id: location,
      location,
      files: path?.split(",/").map((item, key) => (key !== 0 ? "/" + item : item)) || [],
      schedule: DETAUL_CRON_VALUE,
      scheduleEnabled: false,
      path,
      filetype: fileType,
      delimiter: delimeter,
      onError,
      onErrorNumber,
      nullValue,
      nextJob: null,
      errorJob: null,
      disableNextJob: null,
      disableErrorJob: null,
      enableNextJob: false,
      cluster: defaultCluster,
      fieldEnclosedBy,
      encoding,
      hasHeaderRow,
      schemaName,
      tableName,
      append: destinationAction === "existing" && existingTableAction === "append",
      fields: columns.mappings,
      bucket,
      s3Prefix: "",
      docuList,
    };

    //Creating exchange job
    const response = await wsService.createExchangeJob(submitData, Workspace_id);

    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      setOpenJob(false);
    }

    setPrevState(setState, { loading: false });
  };

  const handleSubmit = async (dataSourceObj, connectionObj, createDatasetDetails) => {
    let createDataset = saveAsDataset;
    let table = createDataset ? currentName : tableName;

    if (!saveAsDataset && createDatasetDetails?.saveAsDataset === true) {
      createDataset = true;
      table = createDatasetDetails.tableName;
    }

    if (!validateDestinationCols(columns.mappings)) return;
    else if (
      runningProcesses
        .filter((process) => process.workspace === Workspace_id)
        .some((process) => process.isRunning && process.tableName.toLowerCase() === tableName.toLowerCase())
    ) {
      dispatch(
        showToast({
          message: "Table is already in use for Import, please wait for sometime!",
          type: "error",
        })
      );
    } else {
      if (value === 0) {
        upload(dispatch, {
          Workspace_id,
          uploadedFile,
          delimeter,
          nullValue,
          fieldEnclosedBy,
          encoding,
          hasHeaderRow,
          schemaName: createDataset ? "MODEL" : schemaName,
          tableName: table,
          append,
          fileType,
          destinationAction,
          fields: JSON.stringify(columns.mappings),
          onError,
          onErrorNumber,
          dsFromQuickImport: createDataset,
          ...(createDataset
            ? { dataSource: JSON.stringify(dataSourceObj), connection: JSON.stringify(connectionObj) }
            : {}),
          unloadEnabled,
          unloadConnection,
          unloadPath,
        });

        dispatch(
          showToast({
            message: "Import started, this may take some time",
            type: "success",
          })
        );
        dispatch(hideMappingPopup());
        setShowResents(true);
        clearState();
      } else {
        let bucketName = "";
        let s3Prefix = "";
        if (type.toLowerCase() === "s3") {
          //Setting default values based on url props
          const bucketNameArray = bucket.replace("s3://", "").split("/");

          bucketName = bucketNameArray[0];
          bucketNameArray.shift();
          if (bucketNameArray[bucketNameArray.length - 1] === "") {
            s3Prefix = bucketNameArray.slice(0, bucketNameArray.length - 1).join("/");
          } else {
            s3Prefix = bucketNameArray.join("/");
          }
        }

        loadFromCloudStorage(dispatch, {
          Workspace_id,
          fileName: path,
          location,
          delimeter,
          nullValue,
          fieldEnclosedBy,
          encoding,
          hasHeaderRow,
          schemaName: createDataset ? "MODEL" : schemaName,
          tableName: table,
          destinationAction,
          append: destinationAction === "existing" && existingTableAction === "append",
          fileType,
          fields: columns.mappings,
          bucket: bucketName,
          s3Prefix,
          onError,
          onErrorNumber,
          dsFromQuickImport: createDataset,
          unloadEnabled,
          unloadConnection,
          unloadPath,
          ...(createDataset ? { dataSource: dataSourceObj, connection: connectionObj } : {}),
        });

        dispatch(
          showToast({
            message: "Import started, this may take some time",
            type: "success",
          })
        );
        dispatch(hideMappingPopup());
        setShowResents(true);
        clearState();
      }
    }
  };

  // Filtering Columns everytime there is a change in vlaue of search or data type selection
  const $mappings = useMemo(() => {
    let filteredMappingsCols = [];

    if (filters.search.trim() === "" && filters.dataType === "") {
      return columns.mappings;
    }

    if (filters.search.trim() === "") {
      filteredMappingsCols = columns.mappings.filter(({ type }) => {
        return type === filters.dataType;
      });
    } else if (filters.dataType === "") {
      filteredMappingsCols = columns.mappings.filter(({ source }) =>
        source.toLowerCase().includes(filters.search.toLowerCase())
      );
    } else {
      filteredMappingsCols = columns.mappings.filter(
        ({ source, type }) => source.toLowerCase().includes(filters.search.toLowerCase()) && type === filters.dataType
      );
    }

    return filteredMappingsCols;
  }, [filters.dataType, filters.search, columns.mappings]);

  // checkbox for bulk change
  const selectAll = useMemo(() => ($mappings.length ? $mappings.every((item) => item.isSelected) : false), [$mappings]);
  const intermediate = useMemo(() => !selectAll && $mappings.some((item) => item.isSelected), [selectAll, $mappings]);

  const isBulkChangeRequired = (source, key, value) => {
    if ((selectAll || intermediate) && $mappings?.find((item) => item.source === source)?.isSelected) {
      if (!$mappings?.filter(({ isSelected }) => isSelected)?.every((item) => item[key] === value)) return true;
    }
    return false;
  };

  // This data will be passed to TablePreview component to render it only once
  const $dataToPreview = useMemo(() => {
    return { headers: columns.headers, data: columns.data, fileName: columns.fileName, error: columns.error };
  }, [columns.headers, columns.data, columns.fileName, columns.error]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || !validMapping}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog
        TransitionComponent={Transition}
        open={validMapping}
        sx={{
          "& .MuiDialog-paperFullScreen": {
            maxHeight: "97%",
            width: "97%",
          },
        }}
        fullScreen
      >
        <DialogTitle sx={{ padding: "0" }}>
          <AppBar sx={{ position: "relative" }}>
            <Toolbar className={classes.Toolbar}>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                Sample Data and Mapping
              </Typography>

              {[1, 2].includes(accessRole) ? (
                <>
                  {value === 1 && (
                    <ButtonUI
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (!validateDestinationCols(columns.mappings)) return;
                        setOpenJob(true);
                      }}
                      variant="contained"
                      color="success"
                      style={{ marginRight: 15 }}
                      disabled={loading}
                    >
                      Save As New Job
                    </ButtonUI>
                  )}

                  <ButtonUI
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (saveAsDataset) handleCreateDataSource(displayName, currentName);
                      else setOpenDataSource(true);
                    }}
                    variant="contained"
                    color="success"
                    style={{ marginRight: 15, textTransform: "none" }}
                    disabled={loading}
                  >
                    {saveAsDataset ? `Submit` : `Save as ${DATA_SOURCE.singular}`}
                  </ButtonUI>
                </>
              ) : (
                ""
              )}

              {!saveAsDataset && (
                <ButtonUI
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSubmit}
                  variant="contained"
                  color="success"
                  style={{ marginRight: 15 }}
                  disabled={loading}
                >
                  Submit
                </ButtonUI>
              )}
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleCancel}
                style={{ marginRight: 15 }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ width: "100%", height: "100%" }} className={classes.container}>
            <Box sx={{ mb: 2 }} className={classes.bodyContainer}>
              <Box className={classes.PanelContainer} sx={{ maxHeight: "45%" }} ref={previewTableRef}>
                <PreviewTable columns={$dataToPreview} />
              </Box>
              <Box
                className={classes.PanelContainer}
                sx={{
                  maxHeight: `calc(100% - ${previewTableRef?.current?.clientHeight || 0}px)`,
                  flex: 1,
                  overflowY: "clip",
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems={"baseline"}
                  sx={{ marginBottom: "10px" }}
                  ref={filterRef}
                >
                  <span
                    style={{
                      color: "#ed6c02",
                      padding: "0",
                      margin: "0",
                      marginRight: "20px",
                      marginTop: "15px",
                    }}
                  >
                    Mapping
                  </span>
                  <TextField
                    margin="none"
                    size="small"
                    label="Search Attribute"
                    value={filters.search}
                    onChange={(e) => {
                      setColumns((prev) => ({
                        ...prev,
                        mappings: prev.mappings.map((mapping, i) => {
                          return {
                            ...mapping,
                            isSelected: mapping.source.toLowerCase().includes(e.target.value.toLowerCase())
                              ? mapping.isSelected
                              : false,
                          };
                        }),
                      }));
                      setPrevState(setFilters, { search: e.target.value });
                    }}
                    sx={{
                      width: "280px",
                      mr: "20px",
                      mt: "15px",
                    }}
                    InputProps={
                      filters.search !== ""
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setPrevState(setFilters, { search: "" })} edge="end">
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                  />
                  <TextField
                    sx={{ my: 0, width: "280px", mt: "15px" }}
                    select
                    label="Data Type"
                    size="small"
                    margin="none"
                    value={filters.dataType}
                    helperText={`Type detection based on first ${previewCount} rows`}
                    InputProps={
                      filters.dataType !== ""
                        ? {
                            endAdornment: (
                              <InputAdornment
                                position="start"
                                sx={{
                                  mr: "20px",
                                }}
                              >
                                <IconButton onClick={() => setPrevState(setFilters, { dataType: "" })} edge="end">
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : {}
                    }
                    onChange={(e) => {
                      setColumns((prev) => ({
                        ...prev,
                        mappings: prev.mappings.map((mapping, i) => {
                          return {
                            ...mapping,
                            isSelected: mapping.type === e.target.value ? mapping.isSelected : false,
                          };
                        }),
                      }));
                      setPrevState(setFilters, { dataType: e.target.value });
                    }}
                  >
                    {types.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Paper
                  elevation={3}
                  className={classes.Panel}
                  direction="column"
                  sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    height: `calc(100% - 16px - ${filterRef?.current?.clientHeight || 0}px)`,
                  }}
                >
                  <Box
                    sx={{
                      overflowX: "auto",
                      width: "max(100%, 900px)",
                      height: "100%",
                    }}
                    p={0}
                    m={0}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "white",
                        zIndex: 1000,
                        position: "sticky",
                        top: 0,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          width: "max(3%, 80px)",
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <Checkbox
                          checked={selectAll}
                          indeterminate={intermediate}
                          onChange={(e) => handleSelectAllChange(e)}
                          disabled={$mappings.length === 0}
                          sx={{ ml: "6px" }}
                        />
                        <Typography
                          sx={{
                            position: "absolute",
                            left: "50px",
                            top: { xs: "0", md: "10px" },
                            width: { xs: "60px", md: "80px" },
                            fontWeight: "700",
                            wordWrap: "break-word",
                          }}
                        >
                          Select All
                        </Typography>
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Source Column
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Destination Column
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Data Type
                      </Box>
                      <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                        Length/Format
                      </Box>
                    </Box>

                    {$mappings.length !== 0 ? (
                      <Box className={classes.mappingContainer}>
                        <Virtuoso
                          totalCount={$mappings.length}
                          data={$mappings}
                          ref={virtuosoRef}
                          style={{ height: "100%", width: "100%" }}
                          itemContent={(
                            _index,
                            {
                              source,
                              destination,
                              type,
                              size,
                              format,
                              customFormat,
                              hasError,
                              errorMessage,
                              isSelected,
                            }
                          ) => (
                            <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "flex-start" }} key={source}>
                              <Box sx={{ display: "flex", width: "max(3%, 80px)" }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => updateMapping({ isSelected: e.target.checked }, source)}
                                  sx={{ m: "10px 6px" }}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <TextField
                                  margin="none"
                                  size="small"
                                  fullWidth
                                  placeholder="Source Column"
                                  value={source}
                                  inputProps={{ readOnly: true }}
                                  className={classes.mappingField}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <TextField
                                  margin="none"
                                  fullWidth
                                  placeholder="Destination Column"
                                  value={destination}
                                  size="small"
                                  dataid={destination}
                                  error={hasError}
                                  helperText={hasError ? errorMessage : ""}
                                  onBlur={(e) => {
                                    let hasError = false;
                                    let errorMessage = "";
                                    if (checkReservedKeywords(e.target.value)) {
                                      hasError = true;
                                      errorMessage = "Reserved keyword";
                                    } else if (e.target.value.length > 128) {
                                      hasError = true;
                                      errorMessage = "Max limit: 128 Characters!";
                                    }

                                    updateMapping({ hasError, errorMessage }, source);
                                  }}
                                  onInput={(e) => {
                                    if (e.target.value !== "" && !regexSnowflakeField(e.target.value)) {
                                      e.returnValue = false;
                                      e.preventDefault();
                                      return;
                                    }
                                    updateMapping({ destination: e.target.value }, source);
                                  }}
                                  inputProps={{ maxLength: 128, ...(append ? { readOnly: true } : {}) }}
                                  className={classes.mappingField}
                                  style={{ scrollMarginBlockStart: "20px" }}
                                />
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <FormControl size="small" fullWidth margin="none" sx={{ my: 0 }}>
                                  {append ? (
                                    <TextField
                                      margin="none"
                                      fullWidth
                                      value={type}
                                      size="small"
                                      inputProps={{ readOnly: true }}
                                      className={classes.mappingField}
                                    />
                                  ) : (
                                    <Select
                                      value={types.length ? type : ""}
                                      onChange={(e) => {
                                        if (isBulkChangeRequired(source, "type", e.target.value))
                                          setBulkChange({
                                            open: true,
                                            value: { type: e.target.value, size, format, customFormat },
                                            source,
                                          });
                                        else updateMapping({ type: e.target.value }, source);
                                      }}
                                      className={classes.mappingField}
                                    >
                                      {types.map(({ text, value }) => (
                                        <MenuItem key={value} value={value}>
                                          {text}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </FormControl>
                              </Box>
                              <Box className={classes.mappingHeader} sx={{ m: "10px" }}>
                                <FormControl size="small" fullWidth margin="none" sx={{ my: 0 }}>
                                  <>
                                    {options[type] ? (
                                      <Select
                                        value={options[type] ? size : ""}
                                        onChange={(e) => {
                                          if (isBulkChangeRequired(source, "size", e.target.value))
                                            setBulkChange({
                                              open: true,
                                              value: { type, size: e.target.value, format, customFormat },
                                              source,
                                            });
                                          else updateMapping({ size: e.target.value }, source);
                                        }}
                                        className={classes.mappingField}
                                        disabled={append}
                                      >
                                        {options[type].map(({ value }) => (
                                          <MenuItem key={value.toString()} value={value}>
                                            {value}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    ) : formats[type] ? (
                                      <Grid container>
                                        <Grid
                                          style={{ margin: 0, padding: format === "Custom" ? "0px 4px 0px 0px" : 0 }}
                                          size={{
                                            xs: 12,
                                            sm: format === "Custom" ? 6 : 12,
                                          }}
                                        >
                                          <Select
                                            value={formats[type] ? format : ""}
                                            onChange={(e) => {
                                              if (isBulkChangeRequired(source, "format", e.target.value))
                                                setBulkChange({
                                                  open: true,
                                                  value: { type, size, format: e.target.value, customFormat },
                                                  source,
                                                });
                                              else updateMapping({ format: e.target.value }, source);
                                            }}
                                            fullWidth
                                            className={classes.mappingField}
                                          >
                                            {formats[type].map(({ value }) => (
                                              <MenuItem key={value.toString()} value={value}>
                                                {value}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </Grid>
                                        {format === "Custom" && (
                                          <Grid
                                            size={{
                                              xs: 12,
                                              sm: 6,
                                            }}
                                          >
                                            <TextField
                                              margin="none"
                                              fullWidth
                                              value={customFormat}
                                              size="small"
                                              style={{ margin: 0, padding: "0px 0px 0px 4px" }}
                                              inputProps={{ readOnly: false, maxLength: 30 }}
                                              className={classes.mappingField}
                                              onChange={(e) => updateMapping({ customFormat: e.target.value }, source)}
                                              onBlur={(e) => {
                                                if (isBulkChangeRequired(source, "customFormat", e.target.value))
                                                  setBulkChange({
                                                    open: true,
                                                    value: { type, size, format, customFormat: e.target.value },
                                                    source,
                                                  });
                                              }}
                                              required
                                            />
                                          </Grid>
                                        )}
                                      </Grid>
                                    ) : (
                                      <Select value="" aria-disabled disabled className={classes.mappingField}></Select>
                                    )}
                                  </>
                                </FormControl>
                              </Box>
                            </Box>
                          )}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignContent: "center",
                          alignItems: "center",
                          height: "90%",
                        }}
                      >
                        No records found
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </KMKDialog>
      {openJob && <AddJob loading={loading} onClose={() => setOpenJob(false)} onSave={handleCreateJob} />}
      {openDataSource && (
        <AddDataSource
          loading={loading}
          onClose={() => {
            setOpenDataSource(false);
            setSystemNameError(false);
          }}
          onSave={handleCreateDataSource}
          systemNameError={systemNameError}
        />
      )}
      {bulkChange.open && (
        <ConfirmPopup
          configData={{
            popupTitle: `Confirmation Required`,
            popupMessage: `Apply this changes to all the selected items?`,
            handleCancel: () => handleBulkChange(false),
            handleClick: () => handleBulkChange(true),
            loadingForConfirm: loading,
          }}
        />
      )}
    </>
  );
};

export default Mapping;
